import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import NavigationBar from '../components/NavigationBar';
import NowOverlay from '../components/NowOverlay';
import 'leaflet/dist/leaflet.css';
import './Map.css';
import { getGalleries, getNowEntries, findNowIndex, NOW_PLACEMENT_MAP } from '../api/api';
import L from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { icons } from '../assets/icons';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: icons.starRetina, // Using optimized local import
  iconUrl: icons.star,
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Das NOW-Overlay ueber der Karte blendet langsamer ein als die uebrigen
const MAP_OVERLAY_FADE_IN_MS = 2000;

// Wartezeit, bevor das Fenster einer Markierung nach dem Wegfahren mit der
// Maus schliesst; so laesst sich der Pfeil im Fenster noch erreichen
const POPUP_CLOSE_DELAY_MS = 300;
// Gleiche Lage wie ein an die Markierung gebundenes Popup:
// popupAnchor des Icons [1, -34] plus Leaflet-Standardversatz [0, 7]
const POPUP_OFFSET = [1, -27];

const MapLocation = () => {
  const { type } = useParams();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Cache for custom icons to avoid recreation
  const iconCache = useMemo(() => new Map(), []);

  // Mit Maus: Fenster beim Darueberfahren, Klick oeffnet direkt die Website.
  // Ohne Maus (Handy, Tablet): der Tipp zeigt das Fenster, der Pfeil darin
  // oeffnet die Website.
  const canHover = useMemo(
    () => window.matchMedia('(hover: hover) and (pointer: fine)').matches,
    []
  );
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const popupCloseTimer = useRef(null);

  // NOW-Overlay einmal pro Browser-Sitzung automatisch oeffnen, beginnend mit
  // dem neuesten Beitrag mit Anzeigeort "Karte". Vor/Zurueck blaettert
  // chronologisch durch alle Beitraege des NOW-Archivs.
  const [nowEntries, setNowEntries] = useState([]);
  const [nowStartIndex, setNowStartIndex] = useState(0);
  const [showNowOverlay, setShowNowOverlay] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem('now_overlay_shown')) return;

    getNowEntries().then(data => {
      const list = data?.data || [];
      const start = findNowIndex(list, NOW_PLACEMENT_MAP);
      if (start >= 0) {
        setNowEntries(list);
        setNowStartIndex(start);
        setShowNowOverlay(true);
      }
      sessionStorage.setItem('now_overlay_shown', '1');
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await getGalleries();
        
        // Check if there are any locations for the selected type
        const locationsForType = type ? data.filter(location => location.Type === type) : [];
        
        // If no locations found for the type, use all locations
        const filteredData = locationsForType.length > 0 ? locationsForType : data;

        // Process locations directly without GeoJSON conversion
        const processedLocations = filteredData.map(location => {
          const coordinates = location.Coordinate.split(',').map(coord => parseFloat(coord.trim()));
          return {
            id: location.documentId,
            name: location.Title,
            description: location.Description,
            dates: location.Schedule,
            location: location.Location,
            website: location.Website,
            mapIcon: location.MapIcon,
            type: location.Type,
            coordinates: [coordinates[0], coordinates[1]] // [lat, lng]
          };
        });
        
        setLocations(processedLocations);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError('Failed to load location data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [type]);

  const createCustomClusterIcon = (cluster) => {
    const count = cluster.getChildCount();
    
    // Determine size and color based on the number of points
    let size, bgColor;
    
    if (count < 10) {
      size = 30;
      bgColor = '#1978c8';
    } else if (count < 25) {
      size = 35;
      bgColor = '#ff6600';
    } else {
      size = 40;
      bgColor = '#ff0000';
    }
    
    return L.divIcon({
      html: `<div style="
        background-color: ${bgColor};
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        border: 3px solid white;
        box-shadow: 0 0 5px rgba(0,0,0,0.3);
      ">${count}</div>`,
      className: "custom-cluster-icon",
      iconSize: L.point(size, size)
    });
  };

  const createPopupContent = (location) => {
    return `
      <div class="popup-content">
        <div class="popup-text">
          <h5 class="navigate-text"><strong>${location.name}</strong></h5>
          <p class="navigate-text">${location.location}</p>
        </div>
        <div class="popup-icon">
          <img src="${icons.next}" alt="Icon" />
        </div>
      </div>
    `;
  };

  const hasWebsite = (location) =>
    Boolean(location.website && location.website.trim() !== '');

  const openWebsite = (location) => {
    if (!hasWebsite(location)) return;
    // Track external link click
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'external_link_click',
      location_name: location.name,
      link_url: location.website,
    });
    window.open(location.website, '_blank');
  };

  const cancelPopupClose = () => clearTimeout(popupCloseTimer.current);

  const schedulePopupClose = () => {
    cancelPopupClose();
    popupCloseTimer.current = setTimeout(() => {
      if (popupRef.current && mapRef.current) {
        mapRef.current.closePopup(popupRef.current);
      }
    }, POPUP_CLOSE_DELAY_MS);
  };

  const openMarkerPopup = (location) => {
    cancelPopupClose();
    const map = mapRef.current;
    if (!map) return;
    const current = popupRef.current;
    if (current && current.isOpen() && current.locationId === location.id) return;

    // Beim Darueberfahren die Karte nicht verschieben
    const popup = L.popup({ closeButton: false, autoPan: !canHover, offset: POPUP_OFFSET })
      .setLatLng(location.coordinates)
      .setContent(createPopupContent(location))
      .openOn(map);
    popup.locationId = location.id;
    popupRef.current = popup;

    const element = popup.getElement();
    element?.querySelector('.popup-icon')?.addEventListener('click', () => openWebsite(location));
    if (canHover && element) {
      element.addEventListener('mouseenter', cancelPopupClose);
      element.addEventListener('mouseleave', schedulePopupClose);
    }
  };

  // Get default icon based on location type
  const getDefaultIconByType = (locationType) => {
    const typeIconMap = {
      'Art': icons.art,
      'Film & Theater': icons.film,
      'Architecture': icons.architecture,
      'Literature': icons.literature,
      'History': icons.history
    };
    
    return typeIconMap[locationType] || icons.star;
  };

  // Optimized icon creation with caching and thumbnail preference
  const createCustomIcon = useMemo(() => (location) => {
    const iconKey = location.mapIcon?.formats?.thumbnail?.url || 
                   location.mapIcon?.url || 
                   `default-${location.type}`;
    
    // Return cached icon if available
    if (iconCache.has(iconKey)) {
      return iconCache.get(iconKey);
    }
    
    let iconUrl;
    
    // Priority: thumbnail > small > original > default by type
    if (location.mapIcon?.formats?.thumbnail?.url) {
      iconUrl = `${process.env.REACT_APP_STRAPI_API_URL}${location.mapIcon.formats.thumbnail.url}`;
    } else if (location.mapIcon?.formats?.small?.url) {
      iconUrl = `${process.env.REACT_APP_STRAPI_API_URL}${location.mapIcon.formats.small.url}`;
    } else if (location.mapIcon?.url) {
      iconUrl = `${process.env.REACT_APP_STRAPI_API_URL}${location.mapIcon.url}`;
    } else {
      // Use default icon based on location type
      iconUrl = getDefaultIconByType(location.type);
    }
    
    const customIcon = new L.Icon({
      iconUrl: iconUrl,
      shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    });
    
    // Cache the icon
    iconCache.set(iconKey, customIcon);
    return customIcon;
  }, [iconCache]);

  const nowOverlay = (
    <NowOverlay
      entries={showNowOverlay ? nowEntries : []}
      startIndex={nowStartIndex}
      onClose={() => setShowNowOverlay(false)}
      fadeInMs={MAP_OVERLAY_FADE_IN_MS}
    />
  );

  if (loading) return (
    <>
      {nowOverlay}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    </>
  );

  if (error) return <>{nowOverlay}<p>{error}</p></>;
  if (!locations || locations.length === 0) return <>{nowOverlay}<p>No data available</p></>;

  return (
    <>
      {nowOverlay}
      <NavigationBar />
      <MapContainer
        ref={mapRef}
        center={[52.526716, 13.411683]} 
        zoom={18} 
        style={{ height: "100vh", width: "100%" }} 
        zoomControl={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        
        <MarkerClusterGroup 
          chunkedLoading
          iconCreateFunction={createCustomClusterIcon}
          spiderfyOnMaxZoom={true}
          disableClusteringAtZoom={18}
          maxClusterRadius={50}
        >
          {locations.map((location) => {
            // Use optimized icon creation
            const customIcon = createCustomIcon(location);
            
            return (
              <Marker 
                key={location.id || `marker-${Math.random()}`}
                position={[location.coordinates[0], location.coordinates[1]]}
                icon={customIcon}
                eventHandlers={{
                  mouseover: () => {
                    if (canHover) openMarkerPopup(location);
                  },
                  mouseout: () => {
                    if (canHover) schedulePopupClose();
                  },
                  click: () => {
                    // Track marker click
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                      event: 'marker_click',
                      location_name: location.name,
                      location_type: location.type,
                    });

                    if (canHover && hasWebsite(location)) {
                      openWebsite(location);
                    } else {
                      openMarkerPopup(location);
                    }
                  }
                }}
              >
              </Marker>
            );
          })}
        </MarkerClusterGroup>
      </MapContainer>
    </>
  );
};

export default MapLocation;